import { AlertTriangle, RefreshCcw } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';

import { logger } from '@shared/utils/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** Identificador opcional para contextualizar el log (p.ej. 'dashboard'). */
  name?: string;
}

interface State {
  error: Error | null;
}

/**
 * Error Boundary genérico. Atrapa errores de renderizado y muestra una
 * pantalla de recuperación con botón "Reintentar" (resetea el boundary).
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    logger.error('ErrorBoundary atrapó un error', error, {
      boundary: this.props.name,
      componentStack: info.componentStack,
    });
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div
          role="alert"
          aria-live="assertive"
          className="min-h-[60vh] flex items-center justify-center p-8"
        >
          <div className="max-w-md text-center bg-white rounded-2xl shadow-sm border border-brand-silver/30 p-8">
            <div className="mx-auto w-12 h-12 rounded-full bg-red-50 text-red-600 flex items-center justify-center mb-4">
              <AlertTriangle size={24} aria-hidden="true" />
            </div>
            <h2 className="text-lg font-bold text-brand-dark">Algo salió mal</h2>
            <p className="text-brand-gray mt-2 text-sm">
              Ha ocurrido un error inesperado cargando esta sección. Puedes reintentar o recargar la
              página.
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={this.reset}
                className="inline-flex items-center gap-2 bg-brand-tomato text-white px-4 py-2 rounded-lg font-semibold hover:bg-brand-dim transition-colors"
              >
                <RefreshCcw size={16} aria-hidden="true" />
                Reintentar
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2 text-brand-dark bg-brand-ghost px-4 py-2 rounded-lg font-semibold hover:bg-brand-silver/30 transition-colors"
              >
                Recargar página
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
